import React, { useMemo, useState } from "react";
import { Box, Chip, IconButton, Paper, Tooltip, useTheme } from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import { DataGridPro, GridToolbar, useGridApiRef } from "@mui/x-data-grid-pro";
import { svSE, enUS } from "@mui/x-data-grid/locales";
import { useIntl } from "react-intl";
import { tokens } from "../../../assets/theme";
import cmmsMessages, { getTriggerBasisLabel } from "../../cmms/cmmsMessages";
import { EMPTY_VALUE, formatQuantity, formatTimestamp } from "../../cmms/cmmsFormat";
import { getDataGridPanelSx } from "../../cmms/assetHierarchyStyles";
import { applyColumnWidths, readStoredJson, reorderColumns } from "../../cmms/cmmsGridPrefs";
import { CmmsListSkeleton } from "../../cmms/components/CmmsStates";
import ToolServiceStatusCell from "../../cmms/components/ToolServiceStatusCell";
import { getToolServiceLevelColor, getToolServiceLevelLabel } from "../../cmms/toolServiceStatus";
import messages from "../messages";

const columnOrderKey = "cmmsTools-columnOrder";
const columnWidthsKey = "cmmsTools-columnWidths";
const columnVisibilityKey = "cmmsTools-columnVisibility";

/*
 * The columns hidden unless someone asks for them.
 *
 * The description is the widest text on the grid and the least read: what someone opens this page
 * for is the status, not the prose. Remaining is already inside the status cell as a ratio and in
 * its tooltip as a sentence, so the column is a third rendering of one number. The schedule count
 * only says something on the handful of tools watched twice, and the drawer says it properly.
 *
 * None of this touches searching — toolSearch.js reads the fields off the row, so a hidden column
 * is still a findable one. Nor does it hide an alarm: the status bar and the level chip are both
 * visible by default, and the tiles at the top of the page count what is overdue.
 */
const defaultColumnVisibility = {
  description: false,
  remaining: false,
  scheduleCount: false,
};

const initFromStorage = (cols) => {
  let out = cols;
  const order = readStoredJson(columnOrderKey, null);
  if (order) out = reorderColumns(order, out);
  const widths = readStoredJson(columnWidthsKey, null);
  if (widths) out = applyColumnWidths(widths, out);
  return out;
};

/*
 * Blank rather than a zero, everywhere a value comes off a reading.
 *
 * Most tools in the register have no usage schedule at all, so most of these cells have no answer
 * to give. A zero would read as a tool that has run no hours and made no parts, which is a claim;
 * the dash is the honest absence, and it matches what ToolServiceStatusCell renders next to it.
 */
const emptyUnless = (value, render) =>
  (value === null || value === undefined ? EMPTY_VALUE : render(value));

/*
 * The CMMS tool register.
 *
 * One row per tool, not per reading. The register is what the page is a view of — a tool with no
 * schedule is still a tool — and a tool watched twice would otherwise appear twice while most of
 * the register vanished. The bar shows the worst of a tool's schedules, which is the fold the
 * server already ranks for; the drawer is where all of them are named.
 *
 * Storage, skeleton, toolbar and empty state follow SparePartsDataGrid.
 */
export default function CmmsToolsDataGrid({ rows, isLoading, statusLoading, onOpenDetails, emptyState }) {
  const theme = useTheme();
  const intl = useIntl();
  const colors = tokens(theme.palette.mode);
  const apiRef = useGridApiRef();
  const language = localStorage.getItem("language") || "en";
  const translations = language === "sv" ? svSE : enUS;
  // The saved model wins where it has an opinion, and defaultColumnVisibility fills the rest —
  // the same merge SparePartsDataGrid makes, so a column added later is not switched on unseen
  // for anyone who has already touched the Columns panel.
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
    return { ...defaultColumnVisibility, ...readStoredJson(columnVisibilityKey, null) };
  });
  // Worst first, matching the order the server hands the readings over in. Opening on the tool
  // that needs attention is the whole reason someone came to this page; alphabetical would bury
  // it behind however many healthy tools sort before it.
  const [sortModel, setSortModel] = useState([{ field: "serviceStatus", sort: "desc" }]);

  const baseColumns = useMemo(() => [
    {
      field: "name",
      headerName: intl.formatMessage(messages.columnName),
      flex: 1,
      minWidth: 160,
    },
    {
      field: "description",
      headerName: intl.formatMessage(messages.columnDescription),
      flex: 1,
      minWidth: 180,
      valueGetter: (params) => params.row.description ?? "",
    },
    {
      field: "serviceStatus",
      headerName: intl.formatMessage(cmmsMessages.toolStatusColumn),
      width: 200,
      // Sorts on the number behind the bar, not on the rendered element. -1 rather than 0 for an
      // unwatched tool, the same sentinel toolsDataGrid uses: a tool nothing measures is not a
      // tool at 0% of its interval, and sorting worst-first must not float them to the top.
      valueGetter: (params) => params.row.status?.percentConsumed ?? -1,
      renderCell: (params) => (
        <ToolServiceStatusCell status={params.row.status} isLoading={statusLoading} />
      ),
    },
    {
      field: "level",
      headerName: intl.formatMessage(messages.columnLevel),
      width: 120,
      valueGetter: (params) => params.row.status?.level ?? 0,
      renderCell: (params) => {
        const level = params.row.status?.level;
        if (!level) return EMPTY_VALUE;

        const color = getToolServiceLevelColor(level);

        // Outlined and coloured by severity rather than by getStatusChipSx, which paints an
        // active/inactive pair. There are three levels here and the theme already expresses
        // them — the same three colours the status bar beside it uses.
        return (
          <Chip
            size="small"
            variant="outlined"
            label={getToolServiceLevelLabel(level, intl)}
            sx={{ color, borderColor: color, fontSize: 12, fontWeight: 700, height: 24 }}
          />
        );
      },
    },
    {
      field: "scheduleTitle",
      headerName: intl.formatMessage(messages.columnSchedule),
      flex: 0.8,
      minWidth: 150,
      valueGetter: (params) => params.row.status?.scheduleTitle ?? "",
      renderCell: (params) => params.value || EMPTY_VALUE,
    },
    {
      field: "triggerBasis",
      headerName: intl.formatMessage(messages.columnTriggerBasis),
      width: 130,
      valueGetter: (params) => params.row.status?.triggerBasis ?? null,
      renderCell: (params) => emptyUnless(params.value, (basis) => getTriggerBasisLabel(basis, intl)),
    },
    /*
     * Both units, side by side, whichever one the schedule happens to watch.
     *
     * This pair is what the page was asked for. A reading carries RunningTimeHours and Quantity
     * together on purpose — "the other is context, not noise" — and until now nothing on screen
     * showed them: the status cell renders only the unit its own basis names. A tool watched in
     * cycles still wears out in hours, and the two are not convertible.
     */
    {
      field: "runningTimeHours",
      headerName: intl.formatMessage(messages.columnRunningTimeHours),
      width: 140,
      type: "number",
      valueGetter: (params) => params.row.status?.runningTimeHours ?? null,
      valueFormatter: (params) => emptyUnless(params.value, (v) => formatQuantity(intl, v, 1)),
    },
    {
      field: "quantity",
      headerName: intl.formatMessage(messages.columnQuantity),
      width: 120,
      type: "number",
      valueGetter: (params) => params.row.status?.quantity ?? null,
      valueFormatter: (params) => emptyUnless(params.value, (v) => formatQuantity(intl, v, 0)),
    },
    {
      field: "remaining",
      headerName: intl.formatMessage(messages.columnRemaining),
      width: 130,
      type: "number",
      valueGetter: (params) => params.row.status?.remaining ?? null,
      // Unclamped, matching the server: a negative number is how far past its interval the tool
      // is, and rounding that up to zero is a comfortable lie.
      valueFormatter: (params) => emptyUnless(params.value, (v) => formatQuantity(intl, v, 1)),
    },
    {
      field: "lastServicedAt",
      headerName: intl.formatMessage(messages.columnLastServiced),
      width: 170,
      valueGetter: (params) => params.row.status?.lastServicedAt ?? null,
      // A tool that has never been serviced has no date. formatTimestamp on a null would render
      // "Invalid Date", which reads as a bug rather than as a tool nobody has touched yet.
      valueFormatter: (params) => emptyUnless(params.value, (v) => formatTimestamp(intl, v)),
    },
    {
      field: "scheduleCount",
      headerName: intl.formatMessage(messages.columnScheduleCount),
      width: 120,
      type: "number",
      valueGetter: (params) => params.row.statuses?.length ?? 0,
    },
    {
      field: "history",
      headerName: "",
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      // Kept out of the Columns panel. With no headerName it would list as a blank unlabelled
      // switch, and switching it off would quietly remove the row's only action.
      hideable: false,
      // Its own string rather than cmmsMessages.toolHistoryAction, which /tools uses for the
      // button next to this one. That drawer is history alone; this one leads with the schedules
      // watching the tool, and a screen reader should be told which of the two it is opening.
      renderCell: (params) => (
        <Box onClick={(event) => event.stopPropagation()}>
          <Tooltip title={intl.formatMessage(messages.drawerOpen, { tool: params.row.name })}>
            <IconButton
              size="small"
              aria-label={intl.formatMessage(messages.drawerOpen, { tool: params.row.name })}
              onClick={() => onOpenDetails?.(params.row)}
              sx={{ backgroundColor: colors.primary[700], "&:hover": { backgroundColor: colors.primary[500] } }}
            >
              <HistoryIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ], [intl, colors, statusLoading, onOpenDetails]);

  const columns = useMemo(() => initFromStorage(baseColumns), [baseColumns]);

  const gridSlots = useMemo(
    () => ({
      toolbar: GridToolbar,
      loadingOverlay: () => (
        <Box sx={{ px: 2, pt: 1.5, width: "100%" }}>
          <CmmsListSkeleton rows={8} height={36} />
        </Box>
      ),
      // Only when the caller supplied one — an overlay rendering `undefined` would cover the grid
      // with nothing.
      ...(emptyState ? {
        noRowsOverlay: () => (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
            {emptyState}
          </Box>
        ),
      } : {}),
    }),
    [emptyState],
  );

  const handleColumnOrderChange = () => {
    if (!apiRef.current) return;
    // Every column, not just the visible ones: a hidden column left out here comes back at the
    // far right the moment it is unhidden.
    const fields = apiRef.current.getAllColumns().map((c) => c.field);
    localStorage.setItem(columnOrderKey, JSON.stringify(fields));
  };

  const handleColumnResize = (params) => {
    const map = readStoredJson(columnWidthsKey, {});
    map[params.colDef.field] = params.width;
    localStorage.setItem(columnWidthsKey, JSON.stringify(map));
  };

  const handleColumnVisibilityModelChange = (model) => {
    setColumnVisibilityModel(model);
    localStorage.setItem(columnVisibilityKey, JSON.stringify(model));
  };

  return (
    <Paper sx={{ position: "relative", borderRadius: "4px" }}>
      <Box
        height="70vh"
        sx={{
          "& .MuiDataGrid-root": {
            backgroundColor: colors.primary[400],
            borderColor: `${colors.primary[700]} !important`,
            borderRadius: "0px",
            fontSize: "0.95rem",
            border: "0 !important",
          },
          "& .MuiDataGrid-cell": { borderColor: `${colors.primary[700]} !important` },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.primary[400],
            borderColor: `${colors.primary[700]} !important`,
          },
          "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.primary[600],
          },
          // The toolbar's buttons default to the theme's primary, which here is the surface
          // colour — dark lettering on a dark bar. gray[100] is the foreground token and inverts
          // with the mode.
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.gray[100]} !important`,
          },
          // The row is a link to the drawer, so it should look like one.
          "& .MuiDataGrid-row": { cursor: "pointer" },
        }}
      >
        <DataGridPro
          apiRef={apiRef}
          loading={isLoading}
          slots={gridSlots}
          localeText={translations.components.MuiDataGrid.defaultProps.localeText}
          rows={rows || []}
          getRowId={(row) => row.id}
          columns={columns}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
          onColumnOrderChange={handleColumnOrderChange}
          onColumnResize={handleColumnResize}
          onRowClick={(params) => onOpenDetails?.(params.row)}
          slotProps={{ panel: { sx: getDataGridPanelSx(theme.palette.mode) } }}
          disableRowSelectionOnClick
        />
      </Box>
    </Paper>
  );
}
